/**
 * NatureWelcome — /nature/welcome
 * Long-form introduction article for Nature Pulse.
 */
import { Link } from "react-router-dom";
import { ArrowLeft, Camera, Compass } from "lucide-react";

export default function NatureWelcome() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="nature-welcome">
      <Link to="/nature" className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="w-3 h-3" /> Nature hub
      </Link>
      <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
        WELCOME TO NATURE PULSE
      </div>
      <h1 className="font-display text-4xl md:text-5xl leading-tight mb-2">
        🌿 Discover. Explore. Identify. Protect.
      </h1>
      <p className="text-lg opacity-90 mb-8 leading-relaxed">
        Nature Pulse is Social Pulse's global community dedicated to discovering,
        documenting, and celebrating the extraordinary diversity of life on Earth.
        From ancient forests and majestic mountains to delicate wildflowers, vibrant
        butterflies, soaring birds, coral reefs, winding rivers, pristine beaches,
        and hidden trails, every corner of our planet holds a story waiting to be
        discovered.
      </p>

      <section className="space-y-5 mb-10 opacity-90 leading-relaxed">
        <p>
          Our vision is to create the world's largest community-powered <strong>Global
          Nature Atlas</strong> — a living, evolving knowledge platform that brings together
          artificial intelligence, scientific expertise, citizen science, and geographic
          information to build the most comprehensive and inspiring record of the
          natural world.
        </p>
        <p>
          Whether you are a scientist, educator, student, photographer, gardener, hiker,
          conservationist, traveler, or simply someone who finds joy in nature, Nature
          Pulse gives you the tools to explore, learn, and contribute. Use AI-powered
          identification to discover plants, trees, flowers, birds, insects, fish,
          fungi, wildlife, rocks, and countless other natural wonders.
        </p>
      </section>

      <h2 className="font-display text-2xl mb-3">🌱 Inspiring the Next Generation</h2>
      <div className="space-y-5 opacity-90 leading-relaxed mb-10">
        <p>
          Nature Pulse is a place where curiosity becomes discovery and exploration
          becomes impact. Students, young explorers, and future scientists can use the
          platform to learn about the natural world, develop research skills, and
          contribute to a global knowledge community.
        </p>
        <p>
          With Nature Pulse, you can identify plants and wildlife using AI, document
          local biodiversity, track seasonal changes, map hiking trails, photograph
          native species, and support conservation efforts in your community. Create
          your own digital nature journal, collaborate with classmates and educators,
          share your discoveries through photos, videos, and articles, and build a
          meaningful record of your journey as a nature explorer.
        </p>
        <p>
          The next great discovery does not always begin in a laboratory. It can begin
          with a walk through a neighborhood park, a photograph of a butterfly in your
          backyard, a tree outside your school, or the song of a bird during your
          morning walk.
        </p>
      </div>

      <h2 className="font-display text-2xl mb-3">🌎 Building a Living Record of Earth</h2>
      <div className="space-y-5 opacity-90 leading-relaxed mb-10">
        <p>
          Every species and every natural place deserves to be understood, celebrated,
          and preserved. Nature Pulse aims to create a trusted profile for every plant,
          tree, flower, bird, fish, insect, fungus, animal, ecosystem, and natural
          landmark — enriched with scientific knowledge, local traditions, photographs,
          videos, maps, seasonal observations, and community contributions.
        </p>
        <p>
          But Nature Pulse is more than a digital encyclopedia. It is a global movement
          powered by people who believe that understanding nature is the first step
          toward protecting it.
        </p>
        <p>
          Nature belongs to everyone. Protecting it begins with understanding it. Join
          a worldwide community of explorers, photographers, naturalists, educators,
          and citizen scientists working together to celebrate biodiversity, preserve
          ecological knowledge, support conservation, and inspire a deeper connection
          with the natural world.
        </p>
        <p className="font-medium italic">
          Explore the world. Share your discoveries. Protect our planet — one
          observation at a time.
        </p>
      </div>

      <div className="wofy-card !p-6 text-center">
        <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>GET STARTED</div>
        <h3 className="font-display text-2xl mb-3">Start exploring right now</h3>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Link to="/nature/identify" className="wofy-btn-primary" data-testid="nature-welcome-identify">
            <Camera className="w-4 h-4" /> Identify a photo
          </Link>
          <Link to="/nature" className="wofy-btn-ghost" data-testid="nature-welcome-browse">
            <Compass className="w-4 h-4" /> Browse the Atlas
          </Link>
        </div>
      </div>
    </div>
  );
}
